import { useState } from "react";
import { SEO } from "@/components/SEO";
import { InstLayout, Container, Eyebrow } from "@/components/institutional/InstLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const Demo = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const fd = new FormData(e.currentTarget);
    const payload = {
      institution_name: String(fd.get("institution_name") || "").trim(),
      contact_name: String(fd.get("contact_name") || "").trim(),
      work_email: String(fd.get("work_email") || "").trim(),
      role: String(fd.get("role") || "").trim() || null,
      student_count: String(fd.get("student_count") || "").trim() || null,
      current_lms: String(fd.get("current_lms") || "").trim() || null,
      message: String(fd.get("message") || "").trim() || null,
    };
    if (!payload.institution_name || !payload.contact_name || !payload.work_email) {
      toast({ title: "Missing required fields", description: "Institution, name and work email are required.", variant: "destructive" });
      setSubmitting(false);
      return;
    }
    const { error } = await supabase.from("demo_requests").insert(payload);
    setSubmitting(false);
    if (error) {
      toast({ title: "Submission failed", description: error.message, variant: "destructive" });
      return;
    }
    setSubmitted(true);
  };

  return (
    <InstLayout>
      <SEO
        title="Request a Demo — Behavioral Intelligence for Student Success"
        description="Request a 30-minute walkthrough of the CSW behavioral intelligence platform. We'll show you how engagement signals, risk indicators, and advisor workflows fit your institution."
        canonicalUrl="/demo/"
      />
      <Container>
        <div className="grid lg:grid-cols-2 gap-14 max-w-6xl">
          <div>
            <Eyebrow>Request a demo</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl font-bold leading-[1.05] mb-6">
              See behavioral intelligence on your institution's data.
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              A guided 30-minute walkthrough tailored to your student success goals. We'll cover
              how CSW surfaces engagement signals, supports advisor workflows, and integrates with
              your existing LMS and SIS.
            </p>
            <ul className="space-y-3 text-foreground">
              {[
                "Explainable behavioral signals — no black-box predictions",
                "Privacy-focused data handling and role-based access",
                "Works alongside Canvas, Blackboard, Brightspace, Moodle, and major SIS",
                "Designed to support advisor judgment, not replace it",
              ].map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" aria-hidden={true} />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-3xl border border-border bg-card p-8 md:p-10">
            {submitted ? (
              <div className="text-center py-10">
                <CheckCircle2 className="w-12 h-12 text-primary mx-auto mb-4" aria-hidden={true} />
                <h2 className="font-display text-2xl font-bold mb-3">Thanks — we'll be in touch.</h2>
                <p className="text-muted-foreground">A member of our team will reach out within one business day to schedule your walkthrough.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="institution_name">Institution *</Label>
                  <Input id="institution_name" name="institution_name" required placeholder="University of Example" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_name">Your name *</Label>
                    <Input id="contact_name" name="contact_name" required />
                  </div>
                  <div>
                    <Label htmlFor="work_email">Work email *</Label>
                    <Input id="work_email" name="work_email" type="email" required />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="role">Your role</Label>
                    <Input id="role" name="role" placeholder="Director of Student Success" />
                  </div>
                  <div>
                    <Label htmlFor="student_count">Approx. # of students</Label>
                    <Input id="student_count" name="student_count" placeholder="5,000" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="current_lms">Current LMS</Label>
                  <Input id="current_lms" name="current_lms" placeholder="Canvas, Blackboard, Brightspace…" />
                </div>
                <div>
                  <Label htmlFor="message">Anything you'd like us to know?</Label>
                  <Textarea id="message" name="message" rows={4} placeholder="Tell us about your retention, advising, or FYE goals." />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  size="lg"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold"
                >
                  {submitting ? "Sending…" : "Request my demo"}
                </Button>
                <p className="text-xs text-muted-foreground">
                  By submitting this form you agree to our privacy practices. We'll only use this information to contact you about CSW.
                </p>
              </form>
            )}
          </div>
        </div>
      </Container>
    </InstLayout>
  );
};

export default Demo;
